import { UserMinusIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { useUpdateOrganizationMutation } from '@main/graphql/mutations/UpdateOrganization.admin.generated';
import { GetOrganizationsQuery } from '@main/graphql/queries/GetOrganizations.admin.generated';
import { actionHelper, createColumnHelper, useAlertDialog, useDrawer } from '@main/ui';
import { Row } from '@tanstack/react-table';
import { useMemo } from 'react';

import { STATUSES } from './constants';
import { useUpdateOrganizationHandler } from './use-update-org-handler';

type Organization = GetOrganizationsQuery['organizations'][number];

export function useOrganizationsTableColumns() {
  const drawer = useDrawer();
  const { openDialog } = useAlertDialog();

  const [updateOrganization] = useUpdateOrganizationMutation();
  const updateOrgHanlder = useUpdateOrganizationHandler();

  const { columns, onRowClick } = useMemo(() => {
    const columnHelper = createColumnHelper<Organization>();

    const openOrganizationDrawer = (orgId: string) =>
      drawer.open({ entity: 'organization', entityId: orgId });

    return {
      onRowClick: (row: Row<Organization>) => openOrganizationDrawer(row.original.id),
      columns: [
        columnHelper.columns.status({
          id: 'status',
          header: 'Status',
          accessorFn: ({ disabled }) => {
            const statusMeta = disabled ? STATUSES.disabled : STATUSES.active;

            return {
              value: statusMeta.value,
              tone: statusMeta.tone,
            };
          },
          size: 120,
          enableColumnFilter: true,
        }),

        columnHelper.columns.text({
          id: 'name',
          header: 'Name',
          accessorFn: ({ name }) => name,
          enableGlobalFilter: true,
          enableColumnFilter: true,
          enableHiding: false,
        }),

        columnHelper.columns.text({
          id: 'id',
          header: 'Organization ID',
          accessorFn: ({ id }) => id,
          enableGlobalFilter: true,
          size: 320,
        }),

        columnHelper.columns.tag({
          id: 'type',
          header: 'Type',
          isMulti: true,
          accessorFn: ({ parent_organization_id }) => {
            return [
              parent_organization_id
                ? { value: 'Sub-Org', colorScheme: 'yellow' }
                : { value: 'Main-Org', colorScheme: 'purple' },
            ];
          },
          size: 120,
          enableColumnFilter: true,
          enableGlobalFilter: true,
        }),

        columnHelper.columns.text({
          id: 'users',
          header: 'Users',
          accessorFn: ({ organization_users_aggregate }) =>
            organization_users_aggregate.aggregate?.count.toString(),
          enableColumnFilter: true,
          size: 80,
        }),

        columnHelper.columns.text({
          id: 'programs',
          header: 'Programs',
          accessorFn: ({ programs_aggregate }) => programs_aggregate.aggregate?.count.toString(),
          enableColumnFilter: true,
          size: 100,
        }),

        columnHelper.columns.actions({
          menuActions: ({ row }) => [
            actionHelper.menuActionItem({
              icon: row.original.disabled ? UserPlusIcon : UserMinusIcon,
              label: row.original.disabled ? 'Enable' : 'Disable',
              onClick: () =>
                row.original.disabled
                  ? updateOrgHanlder(
                      updateOrganization({
                        organization_id: row.original.id,
                        updatePayload: {
                          disabled: false,
                        },
                      }),
                    )
                  : openDialog({
                      dialogHeader: 'Disable organization',
                      dialogContent: 'Are you sure you want to disable this organization?',
                      confirmAction: {
                        children: 'Disable',
                        onClick: () =>
                          updateOrgHanlder(
                            updateOrganization({
                              organization_id: row.original.id,
                              updatePayload: {
                                disabled: true,
                              },
                            }),
                          ),
                      },
                    }),
            }),
          ],
        }),
      ],
    };
  }, [drawer, openDialog, updateOrgHanlder, updateOrganization]);

  return {
    columns,
    onRowClick,
  };
}
